import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../../Layout/PageHeader";
import DynamicDetailGrid from "../../../../Components/Grid/DynamicDetailGrid";
import Table, { Column } from "../../../../Components/Tables/Table";
import FormButton from "../../../../Components/Buttons/FormButton";
import { useEffect, useState } from "react";
import { getAllocationPlanDetails } from "../service/allocation.service";
import { AllocationPlanDetails } from "../model/allocation.model";
import Loader from "../../../../Components/GlobalLoader/Loader";
import { toUpperCase } from "../../../../Utility/Utility";

export default function AllocationPlanDetail() {
  const { id } = useParams<{ id: string }>();
  const pageName = `Allocation (${id})`;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allocationPlan, setAllocationPlan] = useState<AllocationPlanDetails | null>(null);

  useEffect(() => {
    fetchAllocationPlanDetails();
  }, [id]);

  const fetchAllocationPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await getAllocationPlanDetails(id!);
      setAllocationPlan(response.data);
    } catch (error) {
      console.error("Error fetching allocation details:", error);
    } finally {
      setLoading(false);
    }
  };

  const allocationConditionData = allocationPlan?.configs?.conditions?.map(
    ([column, operator, value]) => ({
      column,
      operator,
      value,
    })
  ) ?? [];

  const allocationConditionColumns: Column[] = [
    {
      name: "Column",
      selector: (row: { column: string }) => row.column,
    },
    {
      name: "Operator",
      selector: (row: { operator: string }) => row.operator,
    },
    {
      name: "Value",
      selector: (row: { value: string }) => row.value,
    },
  ];

  const allocationOrderByData = allocationPlan?.configs?.order_bys?.map(
    ({ column, dir }) => ({
      column,
      direction: dir,
    })
  ) ?? [];

  const allocationOrderByColumns: Column[] = [
    {
      name: "Column",
      selector: (row: { column: string }) => row.column,
    },
    {
      name: "Direction",
      selector: (row: { direction: string }) => toUpperCase(row.direction),
    },
  ];

  const AllocationPlanOverview = {
    AllocationPlanOverview: [
      {
        label: 'Client',
        value: allocationPlan?.client?.name ? (<>{allocationPlan?.client?.name}({allocationPlan?.client?.code})</>) : '-'
      },
      {
        label: "Allocation ID",
        value: allocationPlan?.id
      },
      {
        label: "Description",
        value: allocationPlan?.description
      },
      {
        label: "Warehouse",
        value: allocationPlan?.warehouse?.name ? (<>{allocationPlan?.warehouse?.name}</>) : '-'
      },
      {
        label: "Inv Status",
        value: allocationPlan?.inventory_statuses?.length
        ? allocationPlan.inventory_statuses.join(', ')
        : '-'
      },
    ],
  };

  if (loading) {
    return <Loader />;
  }
  return (
    <>
      <PageHeader
        listUrl="/outbound/allocations"
        viewType={true}
        isHistoryEnabled={true}
      />

      <h5 className="text-start mt-1 mb-3">{pageName}</h5>
      <div className="my-2 bg-white rounded d-flex flex-column">
        <DynamicDetailGrid
          customClassName="my-2 col-12"
          isExpandable={false}
          columns={4}
          data={AllocationPlanOverview.AllocationPlanOverview}
        />
      </div>

      <h5 className="text-start mt-3 mb-1">Conditions</h5>
      <div className="my-2 bg-white rounded py-3 px-3 waves-table">
        <Table
          columns={allocationConditionColumns}
          data={allocationConditionData ?? []}
          CheckboxStatus={false}
          isStripped={true}
          enableScrollbar={true}
          customClassName="waves-condition-table"
        />
      </div>
      <h5 className="text-start mt-3 mb-1">Order By</h5>
      <div className="my-2 bg-white rounded py-3 px-3 waves-table">
        <Table
          columns={allocationOrderByColumns}
          data={allocationOrderByData ?? []}
          CheckboxStatus={false}
          isStripped={true}
          enableScrollbar={true}
          customClassName="waves-condition-table"
        />
      </div>
      <div className="mt-auto bg-white rounded py-3">
        <div className="d-flex flex-row-reverse">
          <FormButton
            tabIndex={11}
            className="sph-btn me-4"
            handleClick={() => {
              navigate(`/outbound/allocations/edit/${id}`);
            }}
          >
            Edit
          </FormButton>
        </div>
      </div>
    </>
  );
}
